import * as React from 'react'
import { teamSpaceMutators } from '@basehub/data/replicache/mutators/team-space/mutators'
import { useReplicacheQueryV2 } from './use-replicache-query'
import { useTeamReplicacheQuery } from './use-team-replicache'
import { getId } from '@basehub/utils/id/index'
import { toast } from 'sonner'
import { encodeNextParam } from '~/app/(mkt)/_index-redirector/server'
import { useRouter } from 'next/navigation'
import {
  PublicRepo,
  PublicTeam,
} from '@basehub/data/replicache/mutators/_public-types'

export const useBookmark = ({
  team,
  repo,
}: {
  team: Partial<Pick<PublicTeam, 'id' | 'slug'>>
  repo: Partial<Pick<PublicRepo, 'id' | 'slug'>>
}) => {
  const router = useRouter()
  const [bookmark, setBookmark] = React.useState<string | null>(null)
  const replicache = useTeamReplicacheQuery({ forcePersonalTeam: true }).data
    ?.replicache

  const bookmarksQuery = useReplicacheQueryV2({
    replicache,
    fetcher: teamSpaceMutators.listBookmarks,
    key: ['bookmarks', team?.id],
    enabled: Boolean(team?.id) && Boolean(repo.id),
  })

  React.useEffect(() => {
    if (!team?.id) return

    const match = bookmarksQuery.data?.find(
      (b) => b.repoId === repo.id && b.teamId === team?.id
    )
    if (match) {
      setBookmark(match.id)
    }
  }, [setBookmark, repo.id, team?.id, bookmarksQuery])

  const handleToggleBookmark = React.useCallback(async () => {
    if (!team?.id || !team.slug) {
      console.error('Team is not defined.')
      toast.error('Could not add to bookmarks')
      return
    }
    if (!repo.id) {
      console.error('Repository is not defined.')
      toast.error('Could not add to bookmarks')
      return
    }

    if (!replicache) {
      router.push(
        `/login?next=${encodeNextParam('/' + team.slug + '/' + repo.slug)}`
      )
      return
    }

    if (bookmark) {
      await replicache.mutate
        .deleteBookmark(bookmark)
        .then(() => {
          setBookmark(null)
        })
        .catch(() => toast.error('Could not remove from bookmarks'))
    } else {
      try {
        await replicache.mutate.createBookmark({
          id: getId(),
          createdAt: new Date().toISOString(),
          repoId: repo.id,
          teamId: team.id,
        })
      } catch (e) {
        console.error(e)
        toast.error('Could not add to bookmarks')
      }
    }
  }, [team.id, team.slug, repo.id, repo.slug, replicache, bookmark, router])

  return {
    isBookmarked: Boolean(bookmark),
    handleToggleBookmark,
  }
}
